import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useSavedViews, ReportFilters } from '@/hooks/useSavedViews';
import { Save, Trash2, Share2, ChevronDown, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SavedViewsSelectorProps {
  currentFilters: ReportFilters;
  currentGrouping: string;
  onApplyView: (filters: ReportFilters, grouping: string) => void;
}

export const SavedViewsSelector: React.FC<SavedViewsSelectorProps> = ({
  currentFilters,
  currentGrouping,
  onApplyView,
}) => {
  const { views, createView, deleteView } = useSavedViews();
  const { toast } = useToast();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShared, setNewShared] = useState(false);

  const handleSave = async () => {
    if (!newName.trim()) return;
    try {
      await createView(newName.trim(), currentFilters, currentGrouping, newShared);
      toast({ title: 'Vue sauvegardée', description: newName });
      setNewName('');
      setNewShared(false);
      setShowSaveForm(false);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder la vue', variant: 'destructive' });
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await deleteView(id);
      toast({ title: 'Vue supprimée' });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowDropdown(!showDropdown)}>
          Mes vues <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowSaveForm(!showSaveForm)}>
          <Save className="h-3 w-3 mr-1" /> Sauvegarder
        </Button>
      </div>

      {showDropdown && views.length > 0 && (
        <Card className="absolute z-50 mt-1 w-72 shadow-lg">
          <CardContent className="p-2">
            {views.map(v => (
              <div
                key={v.id}
                className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 rounded cursor-pointer text-sm"
                onClick={() => { onApplyView(v.filters, v.grouping); setShowDropdown(false); }}
              >
                <span className="flex items-center gap-1">
                  {v.name}
                  {v.is_shared && <Share2 className="h-3 w-3 text-blue-500" />}
                </span>
                <button onClick={(e) => handleDelete(e, v.id)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {showDropdown && views.length === 0 && (
        <Card className="absolute z-50 mt-1 w-72 shadow-lg">
          <CardContent className="p-4 text-sm text-muted-foreground text-center">
            Aucune vue sauvegardée
          </CardContent>
        </Card>
      )}

      {showSaveForm && (
        <Card className="absolute z-50 mt-1 w-72 shadow-lg">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Sauvegarder la vue</span>
              <button onClick={() => setShowSaveForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <input
              type="text"
              placeholder="Nom de la vue"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={newShared} onChange={e => setNewShared(e.target.checked)} />
              Partagée avec les autres managers
            </label>
            <Button size="sm" onClick={handleSave} disabled={!newName.trim()} className="w-full">
              Sauvegarder
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
